import React, { useState, useEffect, useCallback, useRef } from "react";
import { useDropzone } from "react-dropzone";
import { useQueryClient } from '@tanstack/react-query';
import Stack from "@mui/material/Stack";
import Snackbar from "@mui/material/Snackbar";
import MuiAlert, { AlertColor } from "@mui/material/Alert";
import { Box, Typography, IconButton, Tooltip, Menu, MenuItem, ListItemIcon, ListItemText, Divider, Skeleton, CircularProgress } from "@mui/material";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import GroupIcon from "@mui/icons-material/Group";
import EditIcon from "@mui/icons-material/Edit";
import ExitToAppIcon from "@mui/icons-material/ExitToApp";

import InputBox from "./InputBox";
import MessageComponent from "./MessageComponentLegacy.tsx";
import UserActions from "./UserActions.tsx";
import ConfirmDialog from "./ConfirmDialog.tsx";
import ListMembersDialog from "./ListMembersDialog.tsx";
import UserList from "./UserList.tsx";
import InfoIcon from '@mui/icons-material/Info';
import { IMessage, ContentBlock } from "../../models/Message.ts";
import InfiniteScroll from "react-infinite-scroll-component";
import store from '../../utils/store.ts';
import '../../styles/ChatPanel.css'
import EditDialog from "./EditDialog.tsx";
import { leaveChat, readChat, renameChat, sendMessage, uploadFile } from "../../resources/api.resource.ts";
import { UserMapValue } from "../../models/User.ts";
import TypingIndicator from "./TypingIndicator.tsx";
import { useConversationMessages, addMessageToCache } from "../../hooks/useConversationMessages";
import { useCollaborationAttachments, invalidateAttachmentsCache } from "../../hooks/useCollaborationAttachments";

interface ChatPanelProps {
  groupId?: string;
  uId: string;
  tenantId: string;
  chatType?: "direct" | "group" | "channel" | "project";
  chatTitle: string;
  instanceTitle?: string;
  allUsersMap?: Record<string, UserMapValue>;
  currentUser?: { id: string; name?: string };
  socket?: WebSocket;
  onLeaveRoomSuccess?: () => void;
  onSidebarRefresh?: () => void;
  onMarkAsRead?: (instanceId: string) => void;
  selectedItem: any;
  isLoadingCollaborations?: boolean;
  collaborationError?: string | null;
  subscribeToGroup?: (entry: { groupId: string; summaryId: string }) => void;
  initialDraft?: { html: string; files: File[] };
  onDraftChange?: (draft: { html: string; files: File[] }) => void;
}


// Persists thinking state per conversation so switching away and back preserves the indicator
const thinkingByConversation = new Map<string, boolean>();

const ChatPanelLegacy: React.FC<ChatPanelProps> = ({
  groupId,
  chatType = "direct",
  uId,
  tenantId,
  chatTitle,
  instanceTitle,
  allUsersMap = {},
  currentUser,
  socket,
  onLeaveRoomSuccess,
  onSidebarRefresh,
  onMarkAsRead,
  selectedItem,
  isLoadingCollaborations,
  collaborationError,
  subscribeToGroup,
  initialDraft,
  onDraftChange,
}) => {
  // React Query setup
  const queryClient = useQueryClient();

  // Use the custom hook for message fetching and caching
  const {
    messages,
    isLoading,
    isError,
    error,
    refetch: refetchMessages,
    hasMore,
    fetchNextPage,
    isFetchingNextPage,
  } = useConversationMessages({
    collaborationId: groupId,
    enabled: !!groupId,
  });

  const [files, setFiles] = useState<File[]>(initialDraft?.files ?? []);
  const [sending, setSending] = useState(false); // Loading state for sending messages
  const [notif, setNotif] = useState<{ open: boolean; message: string; severity: AlertColor }>({
    open: false,
    message: "",
    severity: "info",
  });
  const [confirmLeave, setConfirmLeave] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [listMembersOpen, setListMembersOpen] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [title, setTitle] = useState(chatTitle);
  const [drawerType, setDrawerType] = useState<string>("users");
  const [drawerPinned, setDrawerPinned] = useState<boolean>(false);
  const [leaveMsg, setLeaveMsg] = useState("");
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [waitingForAgentResponse, setWaitingForAgentResponse] = useState(false);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const previousGroupIdRef = useRef<string | undefined>(groupId);
  const menuOpen = Boolean(menuAnchorEl);

  const { attachments, isLoading: attachmentsLoading } = useCollaborationAttachments({
    collaborationId: groupId,
    enabled: (drawerOpen || drawerPinned) && drawerType === 'files',
  });
  const { getRootProps, getInputProps } = useDropzone({
    onDrop: useCallback((droppedFiles: File[]) => {
      setFiles((prev) => [...prev, ...droppedFiles]);
    }, [])
  });
  const scrollRef = useRef<HTMLDivElement>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const prevGroupIdRef = useRef<string | undefined>(undefined);
  const prevScrollHeightRef = useRef(0);
  const scrollToBottom = () => {
    setTimeout(() => {
      messagesEndRef.current?.scrollIntoView({ behavior: 'instant' as ScrollBehavior });
    }, 50);
  };

  // Fire fetchNextPage when user scrolls within 150px of the top
  const handleScrollForPagination = useCallback(() => {
    const el = document.getElementById('scrollableDiv');
    if (!el || !hasMore || isFetchingNextPage) return;
    if (el.scrollTop <= 150) {
      prevScrollHeightRef.current = el.scrollHeight;
      fetchNextPage();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasMore, isFetchingNextPage, fetchNextPage]);

  // Add error handling for React Query errors
  useEffect(() => {
    if (isError && error) {
      setNotif({
        open: true,
        message: `Failed to load messages: ${error.message}`,
        severity: "error",
      });
    }
  }, [isError, error]);

  // Scroll to bottom when a new conversation loads its first page of messages.
  // Does not fire during pagination (isFetchingNextPage) so older-page loads
  // don't yank the user back to the bottom.
  useEffect(() => {
    if (messages.length > 0 && !isFetchingNextPage && groupId !== prevGroupIdRef.current) {
      prevGroupIdRef.current = groupId;
      scrollToBottom();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messages.length, groupId, isFetchingNextPage]);

  // After a pagination fetch completes, restore scroll position so the view
  // doesn't jump to the top when older messages are prepended.
  useEffect(() => {
    if (prevScrollHeightRef.current > 0 && !isFetchingNextPage) {
      const el = document.getElementById('scrollableDiv');
      if (el) {
        el.scrollTop = el.scrollHeight - prevScrollHeightRef.current;
        prevScrollHeightRef.current = 0;
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messages.length, isFetchingNextPage]);

  useEffect(() => {
    if(selectedItem === null) {
      setDrawerOpen(false);
      setTitle("");
      setListMembersOpen(false);
      setWaitingForAgentResponse(false);
    }
  }, [selectedItem]);

  // Track when groupId changes to show loading only if not cached
  useEffect(() => {
    if (groupId !== previousGroupIdRef.current) {
      if (groupId) {
        const cached = queryClient.getQueryData(['conversationMessages', groupId]);
        if (!cached) {
          setIsTransitioning(true);
        }
      }
      previousGroupIdRef.current = groupId;
    }
  }, [groupId]);

  // Stop transitioning when messages are loaded
  // Since each conversation has its own cache key (using groupId/instance ID),
  // we don't need to verify message ownership - just wait for loading to complete
  useEffect(() => {
    if (!isLoading && isTransitioning) {
      setIsTransitioning(false);
    }
  }, [isLoading, isTransitioning]);

  // Clear the thinking indicator when the assistant's reply lands in the message cache.
  // This handles the case where the response arrived while the user was viewing a different
  // conversation (the WS handler for this groupId was inactive at that time).
  useEffect(() => {
    if (!groupId || messages.length === 0) return;
    const lastMsg = messages[messages.length - 1];
    if (lastMsg?.sender?.role === 'assistant') {
      thinkingByConversation.delete(groupId);
      setWaitingForAgentResponse(false);
    }
  }, [messages, groupId]);

   useEffect(() => {
    if (!groupId) return;

    // Mark chat as read when opening a conversation
    const markAsRead = async () => {
      try {
        await readChat({ collaboration_id: selectedItem?.collaborationIdSummary, instance_id: groupId });
        if (groupId) {
          onMarkAsRead?.(groupId);
        }
      } catch (err) {
        console.error("Failed to mark chat as read", err);
      }
    };

    markAsRead();

    // Subscribe persistently — the group is never left when switching conversations.
    // All cache updates for this group are handled by usePersistentChatGroups in Workspaces.
    subscribeToGroup?.({
      groupId,
      summaryId: selectedItem?.collaborationIdSummary ?? groupId,
    });

    // Update UI state
    setTitle(chatTitle);
    setDrawerOpen(false);
    setDrawerPinned(false);
    setWaitingForAgentResponse(thinkingByConversation.get(groupId) ?? false);

    // Scroll to bottom when messages load
    scrollToBottom();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupId, socket]);


  const toggleDrawer = (type: string) => {
    setDrawerType(type);
    setDrawerOpen(!drawerOpen);
  }
  useEffect(() => {
    if (!socket || !groupId) return;

    // Get the summary ID for comparison with WebSocket messages
    const summaryId = selectedItem?.collaborationIdSummary ?? groupId;

    const handleMessage = (event: MessageEvent) => {
      try {
        const msg = JSON.parse(event.data);
        if (msg.type === "message" && msg.from === "group" && msg.dataType === "json") {
          const inner = JSON.parse(msg.data);
          // Only handle UI side effects for the currently active conversation.
          // Cache updates for all groups are handled by usePersistentChatGroups.
          if (inner.collaborationId === summaryId || inner.collaborationId === groupId) {
            if (inner.type === "system" && inner.message) {
              scrollToBottom();

              if (inner.event === "user_invited" || inner.event === "user_left" || inner.event === "user_removed") {
                onSidebarRefresh?.();

                if (inner.userId?.toLowerCase() === uId?.toLowerCase()) {
                  // User was removed from or left this group — evict cache and navigate away
                  queryClient.removeQueries({ queryKey: ['conversationMessages', groupId] });
                  setTitle("");
                  setDrawerType("users");
                  setDrawerOpen(false);
                  onLeaveRoomSuccess?.();
                  setNotif({
                    open: true,
                    message: inner.event === "user_removed"
                      ? "You have been removed from the group."
                      : "You left the group.",
                    severity: "warning",
                  });
                }
              }
            } else if (inner.status === 'streaming' && inner.session_id) {
              thinkingByConversation.delete(groupId);
              setWaitingForAgentResponse(false);
              scrollToBottom();
            } else if (inner.sender) {
              if (inner.sender.role === 'assistant') {
                thinkingByConversation.delete(groupId);
                setWaitingForAgentResponse(false);
              }
              scrollToBottom();
            } else if (inner.type === "assistant_thinking") {
              thinkingByConversation.set(groupId, true);
              setWaitingForAgentResponse(true);
              }
        }
        }
      } catch (err) {
        console.error("WebSocket parse error", err);
      }
    };

    socket.addEventListener("message", handleMessage);

    return () => {
      socket.removeEventListener("message", handleMessage);
      // Note: leaveGroup is handled in the joinGroup effect's cleanup
      // to avoid leaving the group when only selectedItem changes
    };
  }, [socket, groupId, queryClient, uId, onSidebarRefresh, onLeaveRoomSuccess, selectedItem?.collaborationIdSummary]);

  const handleSendMessage = async (text?: string, uploadedFiles?: File[], contentBlocks?: ContentBlock[]) => {
    if (!groupId || (!text?.trim() && (!uploadedFiles || uploadedFiles.length === 0))) return;
    setSending(true);
    try {
      // Upload files first if there are any
      const uploadedAttachments = [];
      let uploadFailed = false;
      let failedFileName = "";
      let failedFileReason = "";

      if (uploadedFiles && uploadedFiles.length > 0) {
        for (const file of uploadedFiles) {
          try {
            const resp = await uploadFile(file, groupId);
            if (resp) {
              // API returns: { name, type, url }
              uploadedAttachments.push(resp);
            } else {
              // Upload returned null/undefined - consider it a failure
              uploadFailed = true;
              failedFileName = file.name;
              break;
            }
          } catch (err: any) {
            uploadFailed = true;
            failedFileName = file.name;
            failedFileReason = err.response?.data?.detail?.message || '';
            break;
          }
        }
      }

      // If any file upload failed, abort sending the message
      if (uploadFailed) {
        setNotif({
          open: true,
          message: `Failed to upload ${failedFileName}. ${failedFileReason} Message not sent.`,
          severity: "error"
        });
        throw new Error(`File upload failed: ${failedFileName}`);
      }

      // Build contentBlocks array with attachments
      let finalContentBlocks = contentBlocks || [];

      // If no contentBlocks provided (legacy), create simple text block
      if (!contentBlocks || contentBlocks.length === 0) {
        if (text?.trim()) {
          finalContentBlocks = [{ type: "text", content: text.trim() }];
        }
      }

      // Add file blocks to contentBlocks
      if (uploadedAttachments.length > 0) {
        uploadedAttachments.forEach((attachment: any) => {
          finalContentBlocks.push({
            type: "file",
            fileName: attachment.name,
            url: attachment.url,
            fileType: attachment.type
          });
        });
      }

      // Build clientMetadata
      const clientMetadata = {
        userAgent: navigator.userAgent,
        clientVersion: "2.1.0",
        timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        deviceId: localStorage.getItem('deviceId') || 'web-client',
        ipAddress: "N/A"
      };

      // Build the new message payload matching API schema
      const payload = {
        collaboration_id: groupId,
        content: text?.trim() || "",
        attachments: uploadedAttachments.map((att: any) => ({
          type: att.type,
          url: att.url,
          name: att.name
        })),
        memberUids: [],
        contentBlocks: finalContentBlocks,
        plainTextContent: text?.trim() || "",
        replyToId: null,
        status: "active",
        clientMetadata: clientMetadata
      };

      // Create optimistic message for immediate UI feedback
      const tempId = `temp-${Date.now()}-${Math.random().toString(36).substring(7)}`;
      const optimisticMessage: IMessage = {
        id: tempId,
        pk: tempId,
        collaborationId: groupId,
        tenantId: tenantId,
        createdDateTime: new Date().toISOString(),
        lastEditedDateTime: null,
        sender: {
          id: uId,
          role: 'human',
          displayName: currentUser?.name || 'You',
          avatarUrl: ''
        },
        contentBlocks: finalContentBlocks,
        plainTextContent: text?.trim() || "",
        replyToId: null,
        status: 'active',
        interactions: {
          reactions: {},
          aiQuality: []
        },
        clientMetadata: clientMetadata,
        editHistory: [],
        isPending: true // Mark as pending for blur effect
      };

      // Add optimistic message to cache immediately
      addMessageToCache(queryClient, groupId, optimisticMessage);
      scrollToBottom();

      // Send message to server
      const res = await sendMessage(payload);
      if (!res) throw new Error("Message send failed");

      // Invalidate attachments cache if files were sent
      if (uploadedAttachments.length > 0 && groupId) {
        invalidateAttachmentsCache(queryClient, groupId);
      }

      // Typing indicator is now driven by the assistant_thinking pubsub message
    } catch (err) {
      console.error("Send failed", err);
      // Check if this is a file upload error (we already showed specific error message)
      const isFileUploadError = err instanceof Error && err.message.startsWith('File upload failed:');

      if (!isFileUploadError) {
        // Only show generic error message for non-file-upload errors
        setNotif({ open: true, message: "Failed to send message", severity: "error" });
      }

      // Rethrow the error so InputBox knows the send failed and keeps the message
      throw err;
    } finally {
      setSending(false);
    }
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLButtonElement>) => {
    setMenuAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
  };

  const handleMenuAction = (action: "Manage Members" | "Rename" | "Leave") => {
    if (action === "Manage Members") {
      setListMembersOpen(true);
    } else if (action === "Rename") {
      setShowEditDialog(true);
    } else if (action === "Leave") {
      setConfirmLeave(true);
    }
    handleMenuClose();
  };

  const handleLeaveRoom = async () => {
    if (!groupId) return;
    try {
      const res = await leaveChat({ collaboration_id: groupId });
      if (!res) throw new Error("Leave group failed");
      setNotif({ open: true, message: "You left the room", severity: "warning" });
      setConfirmLeave(false)
      onLeaveRoomSuccess?.();
    } catch (err) {
      console.error(err);
      setNotif({ open: true, message: "Failed to leave", severity: "error" });
    }
  };

  const handleRename = async (name: string) => {
    try {
      const res = await renameChat({ collaboration_id: selectedItem?.collaborationIdSummary ?? groupId, name: name });
      if (!res) throw new Error("Rename failed");
      setNotif({ open: true, message: "Renamed successfully.", severity: "success" });
      setShowEditDialog(false);
      setTitle(name);
      onSidebarRefresh?.();
    } catch (err) {
      console.error(err);
      setNotif({ open: true, message: "Failed to Rename", severity: "error" });
    }
  };

  // Find collaboration by collaborationId or by instance id
  const findCollaboration = (id: string) => {
    return store.getState().collaborations.find((collab) =>
      collab.collaborationId === id ||
      collab.collab_instances?.some((inst: any) => inst.id === id)
    );
  };

  const getGroupName = (): string => {
    return findCollaboration(groupId ?? '')?.name ?? "";
  }

  const getDrawerList = (id: string) => {
    switch(drawerType) {
      case  'users':
        let users: any[] = [];
        let members = findCollaboration(id)?.memberUids;
        if(members && members.length > 0) {
          members.map((member: string) => {
            if(allUsersMap?.[member]?.name) {
              let memberObj = {
                name: allUsersMap?.[member]?.name,
                id: member,
                email: allUsersMap?.[member]?.email,
                role: allUsersMap?.[member]?.userType === 'agent' ? 'assistant' as const : 'human' as const
              }
              users.push(memberObj);
            }

          });
          if (store.getState().user && members.includes(store.getState().user.userId.toLowerCase())) {
             users.push({
              name: store.getState().user.displayName,
              id: store.getState().user.userId,
              role: 'human' as const
             });
          }
        }
        return users;
      case 'files':
        return attachments.map(att => ({
          id: att.id,
          name: att.name,
          url: att.url,
          fileType: att.fileType,
          uploadedBy: att.uploadedByName,
          uploadedAt: att.createdDateTime,
          senderId: att.uploadedBy,
        }));
      default:
        return [];
    }
  }

  const renderEmptyState = () => {
    if (collaborationError) {
      return (
        <Box sx={{
          padding: "40px",
          textAlign: "center",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          height: "100%"
        }}>
          <Typography variant="h6" color="error" gutterBottom>
            Error
          </Typography>
          <Typography variant="body1" color="text.secondary">
            {collaborationError}
          </Typography>
        </Box>
      );
    }

    return (
      <Typography variant="body2" sx={{
        display: 'flex',
        justifyContent: 'center',
        fontSize: '16px',
        color: '#2a2e42',
        fontFamily: 'Proxima Nova',
        fontWeight: 600,
        marginTop: '20px'
      }}>
        {groupId ? "" : "Select a conversation to start chatting"}
      </Typography>
    );
  };

  const renderMessages = () => {
    if (!messages.length) return renderEmptyState();

    return messages.map((msg, idx) => {
      return (
        <div key={msg.id || idx} style={{ marginBottom: "6px" }}>
          <MessageComponent
            key={msg.id || idx}
            message={msg}
            uId={uId}
            chatType={msg.sender.role}
            collaborationId={groupId ?? ""}
            collaborationIdSummary={selectedItem?.collaborationIdSummary}
            tenantId={tenantId}
            allUsersMap={allUsersMap}
            socket={msg.status === 'streaming' ? socket : undefined}
          />
        </div>
      );
    });
  };

  // Show loading overlay when switching to an uncached conversation
  if ((isTransitioning || isLoading) && groupId) {
    return (
      <Stack spacing={0} sx={{ height: "100%", position: 'relative' }}>
        <div className="headerTree">
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <span style={{ fontWeight: "bold" }}>{title}</span>
              {instanceTitle && (
                <>
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>/</Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      color: 'primary.main',
                      fontWeight: 500,
                      fontSize: '0.9rem'
                    }}
                  >
                    {instanceTitle}
                  </Typography>
                </>
              )}
            </Box>
          </div>
        </div>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100%',
          width: '100%',
          backgroundColor: '#f5f5f5'
        }}>
          <div style={{
            textAlign: 'center',
            padding: '40px'
          }}>
            <div style={{
              width: '50px',
              height: '50px',
              border: '4px solid #e0e0e0',
              borderTop: '4px solid #0857c3',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite',
              margin: '0 auto 20px'
            }} />
            <p style={{
              color: '#555',
              fontSize: '16px',
              fontFamily: 'Proxima Nova',
              margin: 0
            }}>
              Loading conversation...
            </p>
            <style>{`
              @keyframes spin {
                0% { transform: rotate(0deg); }
                100% { transform: rotate(360deg); }
              }
            `}</style>
          </div>
        </div>
      </Stack>
    );
  }

  return (
    <Stack spacing={0} sx={{ height: "100%" }}>
      <div
        className="headerTree"
      >
        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <span style={{ color: 'primary.main',
                    fontWeight: 500,
                    fontSize: '0.9rem'}}>{title}</span>
            {instanceTitle && (
              <>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>/</Typography>
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: "bold" ,
 fontSize: '1rem'
                  }}
                >
                  {instanceTitle}
                </Typography>
              </>
            )}
          </Box>
          <IconButton
            size="small"
            onClick={handleMenuOpen}
            className={`headerMenuButton${menuOpen ? ' headerMenuButton--visible' : ''}`}
            sx={{ marginLeft: '4px' }}
          >
            <MoreVertIcon fontSize="small" />
          </IconButton>
        </div>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "12px",
          }}
        >
          <UserActions
            onOpenDrawer={toggleDrawer}
            type={chatType}
            groupId={groupId ?? ""}
          />
        </div>
      </div>

      <Menu
        anchorEl={menuAnchorEl}
        open={menuOpen}
        onClose={handleMenuClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <MenuItem onClick={() => handleMenuAction("Manage Members")}>
          <ListItemIcon>
            <GroupIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="Manage Members" />
        </MenuItem>

        {(chatType === 'group' || chatType === 'project') && (
          <MenuItem onClick={() => handleMenuAction("Rename")}>
            <ListItemIcon>
              <EditIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText primary="Rename" />
          </MenuItem>
        )}

        <Divider />

        <MenuItem onClick={() => handleMenuAction("Leave")}>
          <ListItemIcon>
            <ExitToAppIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="Leave Room" />
        </MenuItem>
      </Menu>
      <div className="chatWrapperWorkspaces" ref={scrollRef} style={{ display: 'flex', position: 'relative', flex: 1, overflow: 'hidden' }} {...getRootProps()}>
        <div className="chatWrapper" style={{ flex: 1, display: 'flex', flexDirection: 'column', height: '100%' }}>

            <div
              id="scrollableDiv"
              style={{
                flex: 1,
                overflow: "auto",
                overflowX: "hidden",
                width: "100%",
              }}
              onScroll={handleScrollForPagination}
            >
              {isFetchingNextPage && (
                <div style={{ display: 'flex', justifyContent: 'center', padding: '8px' }}>
                  <CircularProgress size={24} />
                </div>
              )}
              <InfiniteScroll
                dataLength={messages.length}
                next={() => {}}
                hasMore={false}
                loader={null}
                style={{ overflow: "hidden", padding: "10px", paddingBottom: "60px" }}
                scrollableTarget="scrollableDiv"
              >
                {renderMessages()}
                {leaveMsg && (
                  <div className="leave-message">
                    <InfoIcon />
                    <div className="message-text">{leaveMsg || ""}</div>
                  </div>
                )}
                <div ref={messagesEndRef} />
              </InfiniteScroll>
            </div>

          <div className="footerContainer" style={{ position: 'relative' }}>
            {waitingForAgentResponse && (
              <div style={{
                position: 'absolute',
                bottom: '100%',
                left: '10px',
                marginBottom: '8px'
              }}>
                <TypingIndicator />
              </div>
            )}
            <div className="inputContainer">
              <InputBox
                {...getInputProps()}
                groupId={groupId ?? ""}
                error={undefined}
                onSend={handleSendMessage}
                files={files}
                setFiles={setFiles}
                loading={sending}

                mentionableUsers={getDrawerList(groupId ?? '')}
                initialDraft={initialDraft}
                onDraftChange={onDraftChange}
              />
            </div>
          </div>
        </div>
        {(drawerOpen || drawerPinned) && (
          <Box sx={{
            position: drawerPinned ? 'sticky' : 'absolute',
            top: 0,
            right: 0,
            height: '100%',
            backgroundColor: drawerPinned ? 'white' : '#f5f5f5',
            boxShadow: drawerPinned ? 'none' : '-2px 0 8px rgba(0, 0, 0, 0.15)',
            borderLeft: drawerPinned ? '1px solid rgba(0, 0, 0, 0.12)' : 'none',
            zIndex: drawerPinned ? 'auto' : 1000,
            transition: 'all 0.3s ease-in-out',
          }}>
            <UserList
              refreshSidebar={() => onSidebarRefresh?.()}
              refreshRightPanel={() => {
                return getDrawerList(groupId ?? '')
              }}
              uId={uId}
              drawerType={drawerType}
              showAddRemove={(chatType === 'group' || chatType === 'project')}
              list={getDrawerList(groupId ?? '')}
              listLoading={drawerType === 'files' && attachmentsLoading}
              pinned={drawerPinned}
              onAction={(action) => {
                if (action === "close") {
                  setDrawerOpen(false);
                  setDrawerPinned(false);
                }
                if (action === "pin") {
                  if (drawerPinned) {
                    setDrawerPinned(false);
                    setDrawerOpen(true);
                  } else {
                    setDrawerPinned(true);
                    setDrawerOpen(false);
                  }
                }
              }}
              groupId={groupId ?? ""}
              collaborationId={selectedItem?.collaborationIdSummary ?? groupId ?? ""}
            />
          </Box>
        )}
      </div>

      <Snackbar
        open={notif.open}
        autoHideDuration={3000}
        onClose={() => setNotif({ ...notif, open: false })}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <MuiAlert
          severity={notif.severity}
          variant="filled"
          onClose={() => setNotif({ ...notif, open: false })}
        >
          {notif.message}
        </MuiAlert>
      </Snackbar>

      <ConfirmDialog
        color="error"
        open={confirmLeave}
        title="Leave Room?"
        message="Are you sure you want to leave this room?"
        onConfirm={handleLeaveRoom}
        onClose={() => setConfirmLeave(false)}
      />

      <EditDialog
        open={showEditDialog}
        title="Rename"
        name={getGroupName()}
        onConfirm={handleRename}
        onClose={() => setShowEditDialog(false)}
      />

      <ListMembersDialog
        open={listMembersOpen}
        groupId={groupId ?? ""}
        currentUserId={uId}
        onClose={() => setListMembersOpen(false)}
        onRemoved={(userId) =>
          setNotif({ open: true, message: `${userId} removed from room`, severity: "info" })
        }
        onLeave={() =>
          setNotif({ open: true, message: "You left the room", severity: "warning" })
        }
        onPinclose={()=>setDrawerOpen(false)}
      />
    </Stack>
  );
};

export default ChatPanelLegacy;
